import { createAction } from '@reduxjs/toolkit';
import type { TypeRemovalScope } from '@shared/schemas';

import type { BlockDraft } from '../domain/profile-type.model';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. Kept out of
 * the slice's reducers: they trigger side effects (load / save / clear / run), not
 * the state transitions the saga dispatches as it progresses.
 */

/** Load the types with their blocks (dispatched by the page on mount). */
export const blocksRequested = createAction('profileTypes/blocksRequested');

/**
 * Save the editor's block. Carries the draft, not the payload: the mapping to the API
 * shape is a domain rule (`draftToPayload`), applied by the saga.
 *
 * `thenApply` chains "Review and apply": save first, so what is applied is what is
 * stored, then open the apply dialog on it.
 */
export const saveRequested = createAction<{ draft: BlockDraft; thenApply?: boolean }>(
  'profileTypes/saveRequested',
);

/** Flip a type's Automatic switch from the overview; resends its stored block. */
export const automaticToggleRequested = createAction<{ name: string; autoClassify: boolean }>(
  'profileTypes/automaticToggleRequested',
);

/** Remove a type's block (the page owns the confirmation). The type and its tags stay. */
export const clearRequested = createAction<{ name: string }>('profileTypes/clearRequested');

/**
 * Ask what applying a stored block over the whole base would tag. Dispatched when the
 * apply dialog opens; the count is the gate, since tagging has no undo.
 */
export const runPreviewRequested = createAction<{ name: string }>('profileTypes/runPreviewRequested');

/** Apply a stored block over the whole base (the dialog confirms). */
export const runRequested = createAction<{ name: string }>('profileTypes/runRequested');

/**
 * The editor's live count for the block being written. Dispatched on every edit; the
 * saga waits for a pause in typing and keeps only the latest, so a burst of edits
 * asks the server once.
 */
export const draftPreviewRequested = createAction<{ draft: BlockDraft }>('profileTypes/draftPreviewRequested');

/** Open the bulk removal dialog on a type and measure both options (#652). */
export const removalPreviewRequested = createAction<{ name: string }>('profileTypes/removalPreviewRequested');

/** Take the type off profiles in bulk (the dialog confirms). Logged, so it can be undone. */
export const removalRequested = createAction<{ name: string; scope: TypeRemovalScope }>(
  'profileTypes/removalRequested',
);

/** Load "Recent removals". */
export const removalsRequested = createAction('profileTypes/removalsRequested');

/** Put a bulk-removed type back on exactly the profiles it was taken off. */
export const undoRemovalRequested = createAction<{ id: string }>('profileTypes/undoRemovalRequested');

/** Load the type priority order for the Priority tab (#554). */
export const typePriorityRequested = createAction('profileTypes/typePriorityRequested');

/** Persist the current working order and recompute every profile's primary type. */
export const typePrioritySaveRequested = createAction('profileTypes/typePrioritySaveRequested');
